"use client"

import { useId, useState } from "react"
import { Bug } from "lucide-react"
import { toast } from "sonner"

import {
  Button,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
  Field,
} from "@/components/ui"

import {
  DESCRIPTION_MAX_LENGTH,
  DESCRIPTION_MIN_LENGTH,
  EXPECTED_MAX_LENGTH,
} from "../constants"
import { useBugReport, useRouteTrail } from "../hooks"

const TEXTAREA_CLASS =
  "w-full resize-y rounded-lg border border-neutral-200 bg-neutral-0 px-3 py-2 text-body-sm text-ink-body outline-none placeholder:text-ink-subtle focus-visible:border-primary-600 focus-visible:ring-[3px] focus-visible:ring-ring/50 disabled:opacity-60"

/**
 * Aba de relato, colada na borda direita da janela.
 *
 * Saiu do canto inferior direito porque lá ele era coberto: o
 * `AdminAssistantWidget` ocupa `right-5 bottom-5` com `z-[90]`, e a `MobileNav`
 * e o `Toaster` disputam a mesma quina. A borda lateral, acima do meio, é o
 * único lugar da janela que ninguém mais reivindica.
 *
 * Visível por decisão, não discreto: a primeira versão era um ícone cinza a 60%
 * de opacidade sobre fundo branco e ninguém achava.
 *
 * Âmbar e não vermelho porque vermelho, num checkout, lê como "o pagamento
 * falhou". Âmbar lê como "beta". E o tom é o `feedback-warning-text`, não o
 * `-accent`: o accent dá 3,04:1 com texto branco e 2,86:1 contra o fundo da
 * página, reprovando tanto em texto (4,5:1) quanto em contorno de componente
 * (3:1). Este tom entrega 6,90 e 6,48.
 *
 * `writing-mode: vertical-rl` gira as letras junto com a direção, e o
 * `rotate-180` faz a frase subir em vez de descer — numa aba encostada à
 * direita é assim que se lê sem torcer o pescoço, com o começo embaixo. O giro
 * leva o ícone junto, que por isso aparece na base.
 *
 * No mobile o rótulo some e sobra o ícone: texto vertical numa tela de 375px
 * come altura demais para o que entrega.
 */
export function BugReportButton() {
  useRouteTrail()

  const [open, setOpen] = useState(false)
  const [description, setDescription] = useState("")
  const [expected, setExpected] = useState("")
  const { status, error, submit, reset } = useBugReport()

  const descriptionId = useId()
  const expectedId = useId()

  const sending = status === "sending"
  const canSubmit =
    description.trim().length >= DESCRIPTION_MIN_LENGTH && !sending

  function handleOpenChange(next: boolean) {
    if (sending) return
    setOpen(next)
    if (!next) reset()
  }

  async function handleSubmit(event: React.FormEvent<HTMLFormElement>) {
    event.preventDefault()
    if (!canSubmit) return

    const sent = await submit({ description, expected })
    if (!sent) return

    // Só aqui o texto é descartado: em erro ele continua na tela para o aluno
    // tentar de novo sem reescrever nada.
    setDescription("")
    setExpected("")
    setOpen(false)
    toast.success("Relato enviado. Obrigado por avisar!")
  }

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogTrigger asChild>
        <button
          type="button"
          aria-label="Reportar um problema"
          className="fixed top-1/2 right-0 z-40 inline-flex -translate-y-1/2 rotate-180 items-center justify-center gap-1.5 rounded-none bg-feedback-warning-text px-1 py-2.5 text-[11.5px] font-semibold text-neutral-0 shadow-elevation-2 transition-[padding] duration-200 [writing-mode:vertical-rl] hover:px-2 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-feedback-warning-text motion-reduce:transition-none sm:px-1.5 sm:py-3 sm:hover:px-2.5"
        >
          <Bug className="h-3 w-3 shrink-0" aria-hidden />
          <span className="hidden sm:inline">Reportar um problema</span>
        </button>
      </DialogTrigger>

      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Reportar um problema</DialogTitle>
          <DialogDescription>
            O app está em beta. Conte o que aconteceu — enviamos junto um resumo
            técnico da sua sessão para o time investigar.
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="flex flex-col gap-4">
          <Field
            label="O que aconteceu?"
            required
            htmlFor={descriptionId}
            hint={`${description.length}/${DESCRIPTION_MAX_LENGTH}`}
          >
            <textarea
              id={descriptionId}
              name="description"
              rows={4}
              autoFocus
              required
              disabled={sending}
              maxLength={DESCRIPTION_MAX_LENGTH}
              value={description}
              onChange={(event) => setDescription(event.target.value)}
              placeholder="Ex.: cliquei em continuar na aula 3 e a tela ficou branca."
              className={TEXTAREA_CLASS}
            />
          </Field>

          <Field
            label="O que você esperava? (opcional)"
            htmlFor={expectedId}
            hint="Ajuda a saber se é erro ou comportamento inesperado."
          >
            <textarea
              id={expectedId}
              name="expected"
              rows={2}
              disabled={sending}
              maxLength={EXPECTED_MAX_LENGTH}
              value={expected}
              onChange={(event) => setExpected(event.target.value)}
              placeholder="Ex.: que a próxima aula abrisse."
              className={TEXTAREA_CLASS}
            />
          </Field>

          {error && (
            <p
              role="alert"
              className="text-body-sm rounded-lg border border-feedback-error-border bg-feedback-error-bg px-3 py-2 text-feedback-error-text"
            >
              {error}
            </p>
          )}

          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              disabled={sending}
              onClick={() => handleOpenChange(false)}
            >
              Cancelar
            </Button>
            <Button type="submit" disabled={!canSubmit} isLoading={sending}>
              {sending ? "Enviando…" : "Enviar relato"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  )
}
